import { useCallback, useEffect, useSyncExternalStore } from 'react';

/**
 * Stacking order for the floating surfaces that share the CLI's overlay band: the dock and every
 * tool pulled out of it. They all sit at the same z-index token, so the one in front is whichever
 * was registered or clicked last -- a tool dragged over the dock covers it, and clicking the dock
 * puts it back on top.
 *
 * The lift is capped because antd's own popups (dropdowns, tooltips, popconfirms) sit only so far
 * above the band: past twenty surfaces a dropdown would open underneath one.
 */
const MAX_LIFT = 19;

let order: string[] = [];
const listeners = new Set<() => void>();

const emit = () => listeners.forEach(listener => listener());

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export function raiseSurface(id: string) {
  if (order[order.length - 1] === id) return;
  order = [...order.filter(entry => entry !== id), id];
  emit();
}

function dropSurface(id: string) {
  if (!order.includes(id)) return;
  order = order.filter(entry => entry !== id);
  emit();
}

const liftOf = (id: string) =>
  Math.min(Math.max(order.indexOf(id), 0), MAX_LIFT);

/** A surface's place in the band, and the handler that brings it to the front. */
export function useSurfaceLift(id: string) {
  const lift = useSyncExternalStore(
    subscribe,
    () => liftOf(id),
    () => 0,
  );

  useEffect(() => {
    raiseSurface(id);
    return () => dropSurface(id);
  }, [id]);

  return { lift, raise: useCallback(() => raiseSurface(id), [id]) };
}
